import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box
} from "@mui/material";
import { FileText, X, RefreshCw } from 'lucide-react';
import api from '../utils/api';

// Fonction utilitaire pour formater la date
const formatDate = (dateString) => {
  if (!dateString || dateString === "Jamais") return dateString;
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).replace(',', '');
};

function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openLogs, setOpenLogs] = useState(false);
  const [selectedJob, setSelectedJob] = useState("");
  const [logLoading, setLogLoading] = useState(false);
  const [logContent, setLogContent] = useState("");

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = () => {
    setLoading(true);
    console.log("Récupération des jobs système...");
    api.get("/api/jobs/system-jobs")
      .then((res) => {
        console.log("Données reçues:", res.data);
        setJobs(res.data || []);
      })
      .catch((err) => {
        console.error("Erreur lors de la récupération des jobs:", err);
      })
      .finally(() => setLoading(false));
  };

  const handleOpenLogs = (jobName) => {
    setSelectedJob(jobName);
    setOpenLogs(true);
    fetchLog(jobName);
  };

  const handleCloseLogs = () => {
    setOpenLogs(false);
    setLogContent("");
  };

  const fetchLog = async (jobName) => {
    try {
      setLogLoading(true);
      console.log(`Récupération des logs pour le job ${jobName}...`);
      
      // Récupérer les logs depuis le backend
      const response = await api.get(`/api/jobs/logs`, {
        params: {
          jobName: jobName
        }
      });
      
      console.log("Réponse du serveur:", response.data);
      
      // Vérifier si la réponse contient un log
      let logContent = "";
      if (response.data) {
        if (response.data.log) {
          logContent = response.data.log;
        } else if (typeof response.data === 'string') {
          logContent = response.data;
        } else {
          logContent = JSON.stringify(response.data, null, 2);
        }
      }
      
      if (!logContent || logContent.trim() === "") {
        logContent = `Aucun log disponible pour le job ${jobName}.\n\n` +
                     `Le job a peut-être été lancé récemment et n'a pas encore généré de logs.\n` +
                     `Vous pouvez cliquer sur "Rafraîchir" pour vérifier à nouveau.`;
      }
      
      setLogContent(logContent);
    } catch (error) {
      console.error("Erreur lors du chargement du log:", error);
      
      const errorMessage = `Erreur lors du chargement du log: ${error.message}\n\n` +
                         `Vérifiez que:\n` +
                         `1. Le serveur est en ligne\n` +
                         `2. Le job s'est bien lancé\n` +
                         `3. Le fichier de log existe`;
      
      setLogContent(errorMessage);
    } finally {
      setLogLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px", maxWidth: "900px", margin: "0 auto" }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Jobs System 🚀
      </Typography>

      {loading ? (
        <CircularProgress style={{ display: "block", margin: "20px auto" }} />
      ) : jobs.length === 0 ? (
        <Typography variant="body1" style={{ textAlign: "center", marginTop: "50px" }}>
          Aucun job système trouvé. 
          <Button 
            variant="contained" 
            color="primary" 
            style={{ marginLeft: "10px" }}
            onClick={fetchJobs}
            startIcon={<RefreshCw size={20} />}
          >
            Actualiser
          </Button>
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Nom du Job</strong>
                </TableCell>
                <TableCell>
                  <strong>Dernière Exécution</strong>
                </TableCell>
                <TableCell>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>{job.job_name}</TableCell>
                  <TableCell>
                    {formatDate(job.last_execution)}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenLogs(job.job_name)}
                    >
                      <FileText size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openLogs}
        onClose={handleCloseLogs}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Logs pour {selectedJob}
          <IconButton
            aria-label="close"
            onClick={handleCloseLogs}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {logLoading ? (
            <CircularProgress />
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Dernière mise à jour: {new Date().toLocaleTimeString()}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshCw size={20} />}
                  onClick={() => fetchLog(selectedJob)}
                >
                  Actualiser
                </Button>
              </Box>
              <Box 
                component="pre" 
                sx={{ 
                  whiteSpace: "pre-wrap", 
                  backgroundColor: "#f5f5f5", 
                  padding: 2,
                  borderRadius: 1,
                  maxHeight: "500px",
                  overflow: "auto",
                  fontFamily: "monospace"
                }}
              >
                {logContent}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Jobs; 